// ===========================================================================
// Age proof, stored and reused ON THIS DEVICE for as long as the server signed
// it (two hours by default). After a user verifies their age, the signed age
// token is kept in localStorage, so a Lumi Link opened in a new tab, or from
// another app, does not send somebody who verified ten minutes ago back
// through the selfie check. The token is server-signed (the band cannot be
// spoofed) and short-lived.
//
// History, because this has moved twice. It sat in localStorage for a DAY,
// which made one selfie cover whoever used the browser next. It then moved to
// sessionStorage, per tab, for the reveal work. On 2026-09-12 the operator
// decided a new tab is not a different person, while a different device may
// be, and the two-hour life is the guard on a shared machine. So: per device,
// for two hours, and cleared the moment the token has expired.
//
// verifyAge() is UI-agnostic: callers pass onVideo/onStatus callbacks so the chat
// screen and the Lumi Plus sign-up sidebar can each render it their own way.
// ASCII only.
// ===========================================================================

import { SERVER_HTTP } from './config.js';
import { cameraErrorMessage, lumaVariance, BLANK_VARIANCE } from './camera-errors.js';

const AGE_KEY = 'lumi.age';
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

/** Decode the (unverified) payload of a signed age token to read its band + exp. */
export function decodeAgeToken(token) {
  try {
    const dot = token.indexOf('.');
    if (dot <= 0) return null;
    let b64 = token.slice(0, dot).replace(/-/g, '+').replace(/_/g, '/');
    b64 += '='.repeat((4 - (b64.length % 4)) % 4); // restore base64url padding
    return JSON.parse(decodeURIComponent(escape(atob(b64))));
  } catch {
    return null;
  }
}

/** The stored age proof if present and unexpired, else null (and it is purged). */
export function getStoredAge(nowMs = Date.now()) {
  try {
    // A tab that verified under the previous build kept its proof per tab.
    // Honour it once, by moving it to where proofs now live.
    const tabOnly = sessionStorage.getItem(AGE_KEY);
    if (tabOnly && !localStorage.getItem(AGE_KEY)) localStorage.setItem(AGE_KEY, tabOnly);
    sessionStorage.removeItem(AGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    const raw = JSON.parse(localStorage.getItem(AGE_KEY) || 'null');
    if (!raw || !raw.token || !raw.exp) return null;
    // Expiry is the server's, decoded from the token; an old proof is never
    // presented and never left behind for the next person.
    if (raw.exp * 1000 <= nowMs) {
      localStorage.removeItem(AGE_KEY);
      return null;
    }
    return raw; // { token, exp, band }
  } catch {
    return null;
  }
}

/** Persist an age token, keyed by the exp + band decoded from it. */
export function storeAge(token) {
  const p = decodeAgeToken(token);
  if (!p || !p.exp) return;
  try {
    localStorage.setItem(AGE_KEY, JSON.stringify({ token, exp: p.exp, band: p.band || null }));
  } catch {
    /* storage full / disabled -- non-fatal */
  }
}

export function clearAge() {
  try {
    sessionStorage.removeItem(AGE_KEY);
    localStorage.removeItem(AGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Open the front camera, stepping DOWN through constraints rather than giving
 * up on the first refusal. A 4K hint pinned to the front camera is the ideal
 * and the last two rungs are what a stubborn driver will actually grant; the
 * old single attempt turned a fussy device into "allow the camera".
 * A denied permission stops the ladder at once -- retrying cannot help, and
 * each attempt is another prompt the browser will not show.
 */
async function openSelfieCamera() {
  const res = { width: { ideal: 3840 }, height: { ideal: 2160 } };
  const ladder = [
    { video: { facingMode: { exact: 'user' }, ...res } },
    { video: { facingMode: 'user', ...res } },
    { video: { facingMode: 'user' } },
    { video: true },
  ];
  let lastErr = null;
  for (const constraints of ladder) {
    try {

      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      lastErr = err;
      const name = err && err.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') break;
    }
  }
  throw lastErr || new Error('camera_unavailable');
}

/** Make an Error the caller can offer "Try again" for (vs a hard, terminal failure). */
const retryErr = (msg, extra = {}) => Object.assign(new Error(msg), { retry: true, ...extra });

function blobToBase64(blob) {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onloadend = () => resolve(String(r.result || '').split(',')[1] || '');
    r.onerror = () => resolve('');
    r.readAsDataURL(blob);
  });
}

// What the burst is encoded at. See captureBurst: the models work at 640px and
// 224px, and a reviewer is looking at a face, so anything above this is upload
// time nobody gets anything for.
const CAPTURE_MAX_EDGE = 1280;
const CAPTURE_QUALITY = 0.85;
// The one frame a HUMAN reviews, at the camera's own resolution. Deliberately
// not reduced: judging somebody's age from a face is the whole job, and the
// operator asked for it to stay as sharp as the camera makes it.
const REVIEW_QUALITY = 0.92;

/**
 * Capture a short BURST of frames (active-liveness): a held static photo yields nearly
 * identical frames, a real person responding to the move/blink prompt yields motion.
 * Returns { blobs, maxVariance } -- the variance so the caller can tell a real
 * photo from a stream that never produced one. onTick(i, n) drives the progress copy.
 */
async function captureBurst(stream, n, gapMs, onTick) {
  const v = document.createElement('video');
  v.srcObject = stream;
  v.muted = true;
  v.playsInline = true;
  await v.play().catch(() => {});
  // Wait (up to ~2s) for the first real frame; a black frame is worthless.
  for (let i = 0; i < 40 && !v.videoWidth; i += 1) await delay(50);
  // Capture at a WORKING resolution, not the camera's native one.
  //
  // The burst used to be encoded at full 4K, quality 1.0 -- up to eight
  // megabytes to upload before the server could begin, which was most of the
  // wait people complained about. It bought nothing: the face detector works
  // at 640px wide and the age model at 224x224, so every pixel above that was
  // uploaded, decoded and thrown away. A human reviewing the selfie in the
  // queue is looking at a face, and 1280px of face is more than a face needs.
  const nativeW = v.videoWidth || 1280;
  const nativeH = v.videoHeight || 720;
  const scale = Math.min(1, CAPTURE_MAX_EDGE / Math.max(nativeW, nativeH));
  const w = Math.max(1, Math.round(nativeW * scale));
  const h = Math.max(1, Math.round(nativeH * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  // ONE frame at the camera's full resolution, for the person who reviews the
  // check. The models never see it -- they work at 640px and 224px -- so it
  // costs one frame's upload rather than six, and what a reviewer looks at is
  // exactly as sharp as it was before.
  const fullCanvas = document.createElement('canvas');
  fullCanvas.width = nativeW;
  fullCanvas.height = nativeH;
  const fullCtx = fullCanvas.getContext('2d');
  let reviewBlob = null;
  // A 32x32 copy of each frame, to judge whether anything was drawn at all.
  const probe = document.createElement('canvas');
  probe.width = 32;
  probe.height = 32;
  const pctx = probe.getContext('2d', { willReadFrequently: true });
  let maxVariance = 0;
  const blobs = [];
  for (let i = 0; i < n; i += 1) {
    ctx.drawImage(v, 0, 0, w, h);
    try {
      pctx.drawImage(v, 0, 0, 32, 32);
      maxVariance = Math.max(maxVariance, lumaVariance(pctx.getImageData(0, 0, 32, 32).data));
    } catch {
      maxVariance = Infinity; // never let the probe itself refuse a real photo
    }
    // toBlob can hang on some mobile browsers -- race it against a timeout.
     
    const blob = await Promise.race([
      new Promise((res) => canvas.toBlob(res, 'image/jpeg', CAPTURE_QUALITY)),
      delay(3000).then(() => null),
    ]);
    if (blob) blobs.push(blob);
    // Mid-burst: the first frame often catches somebody still settling.
    if (i === Math.floor(n / 2)) {
      fullCtx.drawImage(v, 0, 0, nativeW, nativeH);

      reviewBlob = await Promise.race([
        new Promise((res) => fullCanvas.toBlob(res, 'image/jpeg', REVIEW_QUALITY)),
        delay(3000).then(() => null),
      ]);
    }
    onTick?.(i + 1, n);
     
    if (i < n - 1) await delay(gapMs);
  }
  return { blobs, maxVariance, reviewBlob };
}

/** Re-encode a list of JPEG blobs at a lower quality/scale (budget fitting). */
async function reencodeBlobs(blobList, quality, scale = 1) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const out = [];
  for (const b of blobList) {
     
    const bmp = await createImageBitmap(b);
    canvas.width = Math.max(1, Math.round(bmp.width * scale));
    canvas.height = Math.max(1, Math.round(bmp.height * scale));
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close?.();
     
    const nb = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
    out.push(nb || b);
  }
  return out;
}

/** One status fetch, bounded: a hung connection must not stall the wait. */
/**
 * @param {string} checkId
 * @param {boolean} [hold]  ask the server to hold the request open until the
 *   check changes (long poll). The decision then arrives as fast as the
 *   response can be written, instead of on the next tick of a backoff that
 *   grows to fifteen seconds -- which is what made "let them in" feel slow
 *   when the deciding itself takes five milliseconds.
 */
async function fetchAgeStatus(checkId, hold = false) {
  const controller = new AbortController();
  // Longer than the server's own hold, so the abort is a real failure rather
  // than the normal end of a held request.
  const timer = setTimeout(() => controller.abort(), hold ? 35000 : 10000);
  try {
    const url = `${SERVER_HTTP}/api/age/status?checkId=${encodeURIComponent(checkId)}${hold ? '&wait=1' : ''}`;
    const res = await fetch(url, { signal: controller.signal });
    return await res.json();
  } catch {
    return null; // offline, or slow: the next poll tries again
  } finally {
    clearTimeout(timer);
  }
}

async function pollAgeStatus(checkId, isCancelled, onMessages, onLive) {
  // No deadline. A check waits for a person for as long as that takes, and so
  // does this loop. The old four-minute give-up threw the person out of the
  // wait with a button that only put them back into it -- and a decision that
  // arrived during the last sleep was never read.
  //
  // THERE IS NO TIMER ON THE HAPPY PATH. The server holds each request open
  // until the check changes, so the moment one comes back the next goes out:
  // sleeping in between would only add that sleep to how long a decision takes
  // to arrive, which is the whole thing this was changed to fix. A gap belongs
  // only after a FAILURE, so a broken connection does not become a hot loop.
  let failures = 0;
  let wake = null;
  const onVisible = () => {
    if (document.visibilityState === 'visible') wake?.();
  };
  document.addEventListener('visibilitychange', onVisible);
  try {
    for (;;) {
      if (isCancelled()) throw new Error('cancelled');
       
      const data = await fetchAgeStatus(checkId, true);
      // null means the request itself failed (offline, or aborted). Everything
      // else is an answer, however uninteresting, and earns an immediate retry.
      if (data == null) {
        failures += 1;
        await new Promise((resolve) => {
          const t = setTimeout(resolve, Math.min(500 * 2 ** (failures - 1), 8000));
          wake = () => {
            clearTimeout(t);
            resolve();
          };
        });
        wake = null;
        continue;
      }
      failures = 0;
      if (data?.status === 'approved' && data.ageToken) return data.ageToken;
      if (data?.status === 'rejected') {
        throw Object.assign(new Error('We could not confirm you are old enough to use Lumi.'), { rejected: true });
      }
      if (data?.status === 'undecided') {
        // A check closed before anyone looked (there is no clock on them any
        // more, but older ones exist). Not a refusal: a fresh photo usually
        // decides on its own, so the button says so.
        throw retryErr('That check was closed before anyone could look at it. Please take a fresh photo.', { undecided: true });
      }
      if (data?.error === 'not_found' || (data?.status && data.status !== 'pending')) {
        // Superseded by a newer check from this device, or gone: this one is
        // over, and waiting on it would be waiting for an answer that never
        // comes.
        throw retryErr('That check was replaced by a newer one. Please take a fresh photo.', { closed: true });
      }
      // The review thread: a moderator may ask the waiting user a question here.
      if (Array.isArray(data?.messages) && data.messages.length) onMessages?.(data.messages);
      // A live-check request: the moderator wants a brief live camera view.
      if (data?.liveOffer) onLive?.({ offer: data.liveOffer, ice: data.ice || null });
      // Straight back out. The server does the waiting.
    }
  } finally {
    document.removeEventListener('visibilitychange', onVisible);
  }
}

// The check currently in the human-review queue, remembered across page loads
// and retries: a phone that locks its screen (suspending the poll) must be able
// to pick up the decision later instead of submitting a fresh selfie -- and
// looping through the queue again -- for a check that was already approved.
const PENDING_KEY = 'lumi.ageCheckId';
function getPendingCheckId() {
  try {
    return localStorage.getItem(PENDING_KEY) || '';
  } catch {
    return '';
  }
}
function setPendingCheckId(id) {
  try {
    if (id) localStorage.setItem(PENDING_KEY, id);
    else localStorage.removeItem(PENDING_KEY);
  } catch {
    /* storage unavailable -- resume just will not work */
  }
}

/** Send the WebRTC answer for a live check; resolves to true on success. */
async function sendLiveAnswer(checkId, answer) {
  try {
    const res = await fetch(`${SERVER_HTTP}/api/age/live-answer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checkId, answer }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Send a reply into the review thread; resolves to the updated thread or null. */
async function sendAgeReply(checkId, text) {
  try {
    const res = await fetch(`${SERVER_HTTP}/api/age/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checkId, text }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return Array.isArray(data.messages) ? data.messages : null;
  } catch {
    return null;
  }
}

/**
 * Run the self-hosted selfie age check and return a signed age token (also stored).
 * @param {number} birthYear
 * @param {string} fingerprint
 * @param {{ onVideo?: (s: MediaStream) => void, onStatus?: (title: string, sub?: string) => void, isCancelled?: () => boolean }} cb
 */
export async function verifyAge(birthYear, fingerprint, cb = {}) {
  const isCancelled = cb.isCancelled || (() => false);

  // Resume: a prior session may have left a check in the review queue (screen
  // locked, tab closed, poll interrupted). Pick up its outcome before taking a
  // new selfie -- a decision made while we were away applies immediately.
  const pendingId = getPendingCheckId();
  if (pendingId) {
    let st = null;
    try {
      st = await (await fetch(`${SERVER_HTTP}/api/age/status?checkId=${encodeURIComponent(pendingId)}`)).json();
    } catch {
      /* offline -- fall through to a fresh check */
    }
    if (st?.status === 'approved' && st.ageToken) {
      setPendingCheckId('');
      storeAge(st.ageToken);
      return st.ageToken;
    }
    if (st?.status === 'rejected') {
      // Decided while they were away. Saying nothing and opening the camera
      // again was the old behaviour, which read as a glitch and cost the
      // person another selfie for the same answer.
      setPendingCheckId('');
      throw Object.assign(new Error('We could not confirm you are old enough to use Lumi.'), { rejected: true });
    }
    if (st?.status === 'pending') {
      cb.onStatus?.(
        "You're in the review queue",
        'Picking up where you left off: a person is double-checking your age. This can take a while. You can leave this page and come back; your place is held and you will not need a new photo.',
      );
      cb.onChatReady?.((text) => sendAgeReply(pendingId, text));
      try {
        const token = await pollAgeStatus(
          pendingId,
          isCancelled,
          (msgs) => cb.onMessages?.(msgs),
          (live) => cb.onLiveCheck?.({ ...live, sendAnswer: (answer) => sendLiveAnswer(pendingId, answer) }),
        );
        setPendingCheckId('');
        storeAge(token);
        return token;
      } catch (err) {
        // ONLY a real rejection forgets the check. A cancel or interruption
        // keeps it, so a decision made after the user walks away still applies
        // the moment they come back -- otherwise an approval decided during
        // their absence is lost and they loop through the queue again.
        if (err?.rejected || err?.undecided || err?.closed) setPendingCheckId(''); // a closed case is not resumed
        throw err;
      }
    }
    if (st?.status === 'undecided') {
      // Say why they are being asked for another photo, rather than silently
      // dropping them back at the camera as though nothing had happened.
      cb.onStatus?.(
        'That check was closed',
        'It was closed before anyone could look at it. Taking a fresh photo now. This usually decides on its own.',
      );
    }
    if (st && st.status) setPendingCheckId(''); // resolved some other way -- start fresh
  }

  let selfie;
  try {
    // Front camera first, at the best size the device will give, stepping down
    // rather than failing on the first refusal. See openSelfieCamera.
    selfie = await openSelfieCamera();
  } catch (err) {
    // Browsers never re-prompt once a camera is blocked, so a message with no
    // instructions is a locked door -- and "allow the camera" is the wrong
    // instruction when the camera is merely busy. Say which it was.
    throw retryErr(cameraErrorMessage(err));
  }
  if (isCancelled()) {
    selfie.getTracks().forEach((t) => t.stop());
    throw new Error('cancelled');
  }
  cb.onVideo?.(selfie);
  // Active liveness: ask for a small movement and grab a short burst so a static photo
  // (near-identical frames) cannot pass, while a real person (motion) does.
  cb.onStatus?.('Look at the camera', 'Slowly turn your head a little, or blink. That is how we confirm it is really you.');
  await delay(500);
  let blobs;
  let maxVariance = 0;
  let reviewBlob = null;
  try {
    // The check needs MOTION (a held photo yields near-identical frames), so the
    // copy has to ask for exactly one thing -- the old "Hold still, keep moving
    // a little" asked for both at once.
    ({ blobs, maxVariance, reviewBlob } = await captureBurst(selfie, 6, 280, (i, n) =>
      cb.onStatus?.('Keep turning your head slowly', `Capturing (${i}/${n})...`),
    ));
  } finally {
    selfie.getTracks().forEach((t) => t.stop());
  }
  // Drop black/empty frames (camera not ready, lens covered).
  let valid = (blobs || []).filter((b) => b && b.size >= 1500);
  if (!valid.length) {
    throw retryErr('We could not capture a clear photo. Make sure your camera is uncovered and well lit, then try again.');
  }
  // The camera granted a stream but sent nothing: every frame one flat colour.
  // Caught here rather than sent, because the estimator can only answer
  // "no face", which reads as "your face was rejected" and is not what
  // happened. Common when another tab or app holds the camera.
  if (maxVariance < BLANK_VARIANCE) {
    throw retryErr(
      'Your camera did not send a picture. Every frame was blank. Close anything else that may be using it (another Lumi tab, a video call), check the lens is uncovered, then try again.',
    );
  }
  cb.onStatus?.('Checking...', 'This only takes a moment.');
  // A backstop, not the usual path any more: captureBurst already encodes at a
  // working resolution, so a burst should be a megabyte or two. This still
  // steps down if some camera or codec produces something enormous -- smaller,
  // never dropped.
  const totalB64 = (list) => (list.reduce((n, b) => n + b.size, 0) * 4) / 3;
  const B64_BUDGET = 11_000_000;
  try {
    for (const [quality, scale] of [[0.85, 1], [0.65, 1], [0.7, 0.7]]) {
      if (totalB64(valid) <= B64_BUDGET) break;
       
      valid = await reencodeBlobs(valid, quality, scale);
    }
  } catch {
    /* re-encode unsupported -- send as captured and let the server judge */
  }
  const frames = (await Promise.all(valid.map(blobToBase64))).filter(Boolean);
  // The full-resolution frame travels separately: the models must not be handed
  // it (that was the slow path), and the server stores it as the selfie.
  const reviewFrame = reviewBlob ? await blobToBase64(reviewBlob) : null;
  // Cancel pressed during the capture used to send the frames anyway, create
  // a check, and remember it for a review the person believed they had
  // called off.
  if (isCancelled()) throw new Error('cancelled');

  // How long to wait for the server's answer.
  //
  // This was 20 seconds, and 22% of real age checks took longer than that --
  // 33 of 148 in one day, with a mean of 15.3s. The nginx log shows them as
  // client-aborted: the browser gave up, showed "the check is taking too
  // long", and threw away a check the server went on to finish and often
  // APPROVE. The person was told their connection had failed while the server
  // was letting them in.
  //
  // The time is not the estimator, which answers a frame in about 0.4s. It is
  // the upload: a burst of frames plus a full-resolution review frame, off a
  // phone, on whatever signal the phone has. So the budget has to cover a slow
  // uplink, and 60s clears every real check observed (the slowest was 55s).
  // A person is told what is happening at 10 and 25 seconds rather than being
  // left watching a spinner.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 60000);
  const slowNotes = [
    setTimeout(() => cb.onStatus?.('Still checking...', 'Sending your photos. This can take a moment on a phone.'), 10000),
    setTimeout(() => cb.onStatus?.('Still checking...', 'A slow connection makes this take longer. Please keep this page open.'), 25000),
  ];
  let res;
  try {
    res = await fetch(`${SERVER_HTTP}/api/age/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        birthYear,
        fingerprint,
        frames,
        reviewFrame,
        escalate: !!cb.escalate,
        // So the reviewer sees WHO they are deciding about.
        handle: cb.handle || undefined,
        accountToken: cb.accountToken || undefined,
      }),
      signal: controller.signal,
    });
  } catch {
    throw retryErr('The check is taking too long. Please check your connection and try again.');
  } finally {
    for (const t of slowNotes) clearTimeout(t);
    clearTimeout(timer);
  }
  if (res.status === 403) {
    const b = await res.json().catch(() => ({}));
    if (b.error === 'under_age') throw new Error(b.message || 'You must be at least 13 to use Lumi.');
    throw new Error('Access to Lumi has been restricted from this device.');
  }
  if (res.status === 400) {
    // image_rejected (nsfw) / liveness_failed / bad image -- a retake can fix it.
    const b = await res.json().catch(() => ({}));
    throw retryErr(b.message || 'That photo was not accepted. Please retake it facing the camera in good light.', { refusal: true });
  }
  if (res.status === 429) {
    // The server says how long; "could not be completed" hid that and the
    // Try again button walked straight back into the limit.
    const b = await res.json().catch(() => ({}));
    throw retryErr(b.message || 'Too many attempts. Please wait a few minutes and try again.');
  }
  if (!res.ok) throw retryErr('Age check could not be completed. Please try again.');
  const data = await res.json();
  if (data.status === 'approved') {
    setPendingCheckId(''); // any older check is moot now
    storeAge(data.ageToken);
    return data.ageToken;
  }
  if (data.status === 'retake') {
    setPendingCheckId('');
    // The model could not read a clear face -- let the user immediately retake.
    throw retryErr(data.message || 'We could not get a clear read of your face. Please retake the photo in good light, facing the camera.', { refusal: true });
  }
  if (data.status === 'pending') {
    setPendingCheckId(data.checkId); // survive interruption; see the resume path above
    cb.onStatus?.(
      "You're in the review queue",
      'A person is double-checking your age. This can take a while. You can leave this page and come back later: your place is held, and you will not need a new photo.',
    );
    // While waiting, a moderator may ask a question; the UI gets a sender for
    // replies and a stream of thread updates from the status poll. A live-check
    // request (SDP offer + ICE) is delivered the same way.
    cb.onChatReady?.((text) => sendAgeReply(data.checkId, text));
    try {
      const token = await pollAgeStatus(
        data.checkId,
        isCancelled,
        (msgs) => cb.onMessages?.(msgs),
        (live) => cb.onLiveCheck?.({ ...live, sendAnswer: (answer) => sendLiveAnswer(data.checkId, answer) }),
      );
      setPendingCheckId('');
      storeAge(token);
      return token;
    } catch (err) {
      if (err?.rejected || err?.undecided || err?.closed) setPendingCheckId(''); // a closed case is not resumed
      throw err;
    }
  }
  throw retryErr('Age check could not be completed. Please try again.');
}

/**
 * Return a valid age token without re-prompting when possible:
 *   1. a stored, unexpired token;
 *   2. self-declared mode hands one back from /api/session (no selfie);
 *   3. otherwise run the selfie verification (verified mode).
 * Returns the token (any band -- the caller checks), or throws on under-age/cancel.
 */
export async function ensureAgeToken(birthYear, fingerprint, cb = {}) {
  const stored = getStoredAge();
  if (stored) return stored.token;

  try {
    const r = await fetch(`${SERVER_HTTP}/api/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ birthYear, fingerprint }),
    });
    if (r.status === 403) {
      const b = await r.json().catch(() => ({}));
      if (b.error === 'under_age') throw new Error(b.message || 'You must be at least 13 to use Lumi.');
    } else if (r.ok) {
      const d = await r.json();
      if (d.ageToken) {
        storeAge(d.ageToken);
        return d.ageToken;
      }
    }
    // 401 verification_required -> fall through to the selfie verification.
  } catch (e) {
    if (e && /least 13/.test(String(e.message))) throw e;
  }

  return verifyAge(birthYear, fingerprint, cb);
}

export default { decodeAgeToken, getStoredAge, storeAge, clearAge, verifyAge, ensureAgeToken };
